import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams, generatePath } from 'react-router-dom';
import {
  ValidatorsProps,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { changePanelState } from '../../store/slices/primary-accounts';
import { useCreatePrimaryAccountMutation } from '../../store/services/primary-accounts';
import useChartForm from '../../hooks/use-form';

import ROUTES from '../../../../routes';

const useCreate = () => {
  const {
    accounts: { typeOptions, panelIsOpen, primaryValidation, accounts },
    categories: { categories },
    groups: { groups },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);
  const [globalError, setGlobalError] = useState('');

  const dispatch = useAppDispatch();

  const { primaryAccountId, categoryId, groupId } = useParams();
  const [create, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useCreatePrimaryAccountMutation();

  const {
    category,
    control,
    handleSubmit,
    reset,
    errors,
    setValue,
    description,
  } = useChartForm({
    groups,
    categories,
    panelIsOpen,
    schema: primaryValidation as ValidatorsProps,
  });

  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setGlobalError('');
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: {
      description: string;
      groupId: string;
    } = {
      description: values.description as string,
      groupId: groupId as string,
    };

    const response = (await create(body)) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (groupId) {
      reset({ groupId });
    }
  }, [reset, groupId]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    accounts,
    activePermissions,
    globalError,
    requestInProgress,
    category,
    control,
    errors,
    setValue,
    description,
    onSubmit,
    groups,
    groupId,
    panelIsOpen,
    handleClose,
  };
};

export default useCreate;
